import React, { useEffect, useRef, useState } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import axiosInstance from "../axiosConfig";
import Select from "react-select";
import Swal from "sweetalert2";
import loaderGif from "../assets/Img/loading1.gif";
import "../assets/CSS/Dashboard.css";

export default function SheetQuantityLeatherExcel() {

  /* ================= BASIC ================= */
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  const user = JSON.parse(localStorage.getItem("user")) || {};
  const user_type = user.Auth;

  /* ================= UPLOAD ================= */
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  /* ================= LISTING ================= */
  const [data, setData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);

  /* ================= FILTER ================= */
  const [excelNames, setExcelNames] = useState([]);
  const [excelFilter, setExcelFilter] = useState("");
  const [sheetFilter, setSheetFilter] = useState("");
  const [lotFilter, setLotFilter] = useState("");

  const excelOptions = excelNames.map(e => ({
    value: e,
    label: e
  }));

  /* ================= FETCH LIST ================= */
  const fetchList = async (excel = "", sheet = "", lot = "") => {
    try {
      setLoading(true);

      let url = "/api/sheet-quenty-leather?upload_type=excel&";
      if (excel) url += `excel_name=${encodeURIComponent(excel)}&`;
      if (sheet) url += `sheet_name=${encodeURIComponent(sheet)}`;
      if (lot) url += `lot_id=${encodeURIComponent(lot)}`;

      const res = await axiosInstance.get(url);
      setData(res.data || []);

    } catch (err) {
      console.error("Excel list load failed", err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= MASTER LOAD ================= */
  const loadMasterData = async () => {
    const res = await axiosInstance.get(
      "/api/sheet-quenty-leather?upload_type=excel"
    );

    const list = res.data || [];
    setAllData(list);
    setData(list);

    setExcelNames([
      ...new Set(list.map(r => r.excel_name).filter(Boolean))
    ]);
  };

  useEffect(() => {
    loadMasterData();
    fetchList();
  }, []);

  /* ================= EXCEL UPLOAD ================= */
  const handleExcelUpload = async () => {
    if (!selectedFile) return;

    const fd = new FormData();
    fd.append("excelFile", selectedFile);

    try {
      setUploading(true);

      const res = await axiosInstance.post(
        "/api/excel-process",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      Swal.fire("Success", res.data.message, "success");

      setSelectedFile(null);
      fileInputRef.current.value = "";

      loadMasterData();
      fetchList();

    } catch (err) {
      Swal.fire(
        "Error",
        err?.response?.data?.error || "Excel upload failed",
        "error"
      );
    } finally {
      setUploading(false);
    }
  };

  /* ================= DELETE ================= */
  const handleDeleteSheet = async (sheetId) => {
    const result = await Swal.fire({
      title: "Delete this sheet?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      await axiosInstance.delete(`/api/sheet-quenty-leather/${sheetId}`);
      Swal.fire("Deleted", "Sheet deleted successfully", "success");
      fetchList(excelFilter, sheetFilter, lotFilter);
      loadMasterData();
    } catch (err) {
      Swal.fire("Error", "Delete failed", "error");
    }
  };

  /* ================= UI ================= */
  return (
    <div className="d-flex dashboard">

      {/* ===== LEFT MENU ===== */}
      <div className={isSidebarVisible ? "sidebar-container" : "sidebar-hidden"}>
        <Sidebar />
      </div>

      {/* ===== RIGHT ===== */}
      <div className="flex-grow-1">
        <Header
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
        />

        <main className="main-container p-4">

          <h3 className="mb-4">Sheet Quantity – Excel</h3>

          {/* ================= UPLOAD ================= */}
          {(user_type === 'Admin' || ["1021","1022","90","3014","260"].includes(user?.EmployeeID)) && (
            <div className="card p-3 mb-4">
              <label className="form-label fw-bold">Upload Excel</label>

              <input
                type="file"
                accept=".xlsx,.xls"
                className="form-control"
                ref={fileInputRef}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;

                  const isExcel =
                    file.type ===
                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                    file.type === "application/vnd.ms-excel" ||
                    file.name.toLowerCase().endsWith(".xlsx") ||
                    file.name.toLowerCase().endsWith(".xls");

                  if (!isExcel) {
                    Swal.fire(
                      "Invalid File",
                      "Only Excel files (.xlsx, .xls) are allowed",
                      "error"
                    );
                    e.target.value = "";
                    setSelectedFile(null);
                    return;
                  }

                  setSelectedFile(file);
                }}
              />

              <button
                className="btn btn-success mt-2"
                disabled={!selectedFile || uploading}
                onClick={handleExcelUpload}
              >
                {uploading ? "Processing Excel..." : "Upload Excel"}
              </button>
            </div>
          )}

          {/* ================= FILTER ================= */}
          <div className="d-flex align-items-center gap-3 mb-4">

            <Select
              className="w-25"
              options={excelOptions}
              isClearable
              placeholder="Filter by Excel"
              value={
                excelOptions.find(o => o.value === excelFilter) || null
              }
              onChange={(opt) => {
                const v = opt ? opt.value : "";
                setExcelFilter(v);
                fetchList(v, sheetFilter, lotFilter);
              }}
            />

            <input
              type="text"
              className="form-control w-25"
              placeholder="Filter Sheet Name"
              value={sheetFilter}
              onChange={(e) => {
                setSheetFilter(e.target.value);
                fetchList(excelFilter, e.target.value, lotFilter);
              }}
            />

            {/* Lot Filter */}
            <input
              type="text"
              placeholder="Filter by Lot ID"
              className="form-control w-25"
              value={lotFilter}
              onChange={(e) => {
                const v = e.target.value;
                setLotFilter(v);
                fetchList(excelFilter, sheetFilter, v);
              }}
            />

            <div className="ms-auto fw-semibold text-muted">
              Total: {data.length}
            </div>
          </div>

          {/* ================= LOADER ================= */}
          {loading && (
            <div className="card d-flex justify-content-center align-items-center"
              style={{ minHeight: 300 }}>
              <img src={loaderGif} alt="loading" width={120} />
            </div>
          )}

          {/* ================= EMPTY ================= */}
          {!loading && data.length === 0 && (
            <div className="card d-flex justify-content-center align-items-center"
              style={{ minHeight: 300 }}>
              <div className="text-muted fw-semibold">
                No Excel data found
              </div>
            </div>
          )}

          {/* ================= LIST ================= */}
          {!loading && data.map(sheet => {
            const nestingSurface = Number(sheet?.nesting_surface_cm2 || 0);
            const meta = sheet?.meta?.[0]; // ✅ safe access

            return (
              <div key={sheet.sheet_id} className="card mb-3 shadow-sm">
                <div className="card-body">
                  <div className="row align-items-center gy-2">

                    {/* ===== LEFT INFO ===== */}
                    <div className="col-md-3">
                      <div className="fw-bold">
                        Sheet: {sheet.sheet_name || "-"}
                      </div>
                      <div className="text-muted small">
                        Lot: {sheet.lot_id || "-"}
                      </div>
                    </div>

                    {/* ===== SURFACE ===== */}
                    <div className="col-md-2">
                      <div className="fw-semibold text-secondary">Nested</div>
                      <div>{nestingSurface}</div>
                    </div>

                    {/* ===== STANDARD ===== */}
                    <div className="col-md-2">
                      <div className="fw-semibold text-secondary">
                        Vendor decimeter
                      </div>
                      <div>
                        {meta?.vendor_decimeter ?? "N/A"}
                      </div>
                    </div>

                    {/* ===== COLOR ===== */}
                    <div className="col-md-3">
                      <div className="fw-semibold text-secondary">Color</div>
                      <div>
                        {meta?.color ?? "N/A"}
                      </div>
                    </div>

                    {/* ===== DELETE ===== */}
                    {user_type === "Admin" && (
                      <div className="col-md-2 text-end">
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeleteSheet(sheet.sheet_id)}
                        >
                          Delete
                        </button>
                      </div>
                    )}

                  </div>
                </div>
              </div>
            );
          })}

        </main>
      </div>
    </div>
  );
}
